import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { PrimaryButton, SecondaryButton } from '@/components/index';

export interface HostFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; uri: string }) => Promise<void>;
}

interface FormData {
  name: string;
  uri: string;
}

export const HostFormDialog: React.FC<HostFormDialogProps> = ({
  open,
  onClose,
  onSubmit
}) => {
  const { control, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<FormData>({
    defaultValues: {
      name: '',
      uri: ''
    }
  });

  const onFormSubmit = async (data: FormData) => {
    try {
      await onSubmit(data);
      reset();
      onClose();
    } catch (error) {
      // Error handling is done in the parent component/hook
      console.error('Form submission failed:', error);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Host</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }} component="form" onSubmit={handleSubmit(onFormSubmit)}>
          <Controller
            name="name"
            control={control}
            rules={{ 
              required: 'Host name is required',
              validate: (value) => value.trim() !== '' || 'Host name cannot be empty'
            }}
            render={({ field }) => (
              <TextField
                {...field}
                autoFocus
                margin="dense"
                label="Host Name"
                fullWidth
                variant="outlined"
                required
                error={!!errors.name}
                helperText={errors.name?.message || "Enter a unique name for the host (e.g., 'bol', 'api-gateway')"}
                sx={{ mb: 2 }}
              />
            )}
          />
          <Controller
            name="uri"
            control={control}
            rules={{ 
              required: 'URI is required',
              validate: (value) => {
                if (!value.trim()) {
                  return 'URI cannot be empty';
                }
                try {
                  new URL(value);
                  return true;
                } catch {
                  return 'Please enter a valid URL (e.g., https://example.com)';
                }
              }
            }}
            render={({ field }) => (
              <TextField
                {...field}
                margin="dense"
                label="URI"
                fullWidth
                variant="outlined"
                required
                error={!!errors.uri}
                helperText={errors.uri?.message || "Enter the full URI for the host (e.g., https://example.com)"}
                placeholder="https://example.com"
                sx={{ mb: 2 }}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <SecondaryButton onClick={handleClose} disabled={isSubmitting}>Cancel</SecondaryButton>
        <PrimaryButton
          onClick={handleSubmit(onFormSubmit)}
          type="submit"
          disabled={isSubmitting}
        >
          Create
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};